import { getBalanceSheet } from "../apiClient";
import { resolveReportDates } from "./dateUtils";
import { formatBalanceSheetData } from "./formatters/balanceSheet";
import { formatReportApiError, isRequestCancelled } from "./errorUtils";
import { getMockReportData } from "./mockData";
import { mapApiReportKeyToType } from "./reportKeys";
import { handleActivityProfitability } from "./reports/activityProfitability";
import { handleCashFlow } from "./reports/cashFlow";
import { handleGeneralReport } from "./reports/general";
import { handlePayablesReport } from "./reports/payables";
import { handleVerticalIncome } from "./reports/profitAndLoss";
import { handleWorkingCapitalLiquidity } from "./reports/workingCapitalLiquidity";

const ACTIVITY_REPORTS = new Set(["activity", "profitability", "receivables", "inventory", "eva", "capitalAllocation", "dupont", "earningsQuality"]);
const LIQUIDITY_REPORTS = new Set(["workingCapital", "liquidity", "workingCapitalPressure", "volatility"]);
const GENERAL_REPORTS = new Set(["general", "trend", "ccc", "strategicMap", "riskModel", "sustainability", "correlation"]);
const BALANCE_REPORTS = new Set(["solvency", "horizontalBalance", "financialIndependence", "financialStability", "accountMovement", "verticalBalance"]);
const INCOME_REPORTS = new Set(["verticalIncome", "earningsStability", "breakEven", "efficiency", "expense", "seasonality", "sensitivity", "forecasting", "scenario", "variance", "revenue"]);

export const generateReport = async (level, reportType, filters, language = "ar", options = {}) => {
  try {
    const hasPeriod = filters?.period || (filters?.periodFrom && filters?.periodTo);
    if (!filters?.currency || !hasPeriod) throw new Error("العملة والفترة مطلوبتان");

    const actualReportType = mapApiReportKeyToType(reportType);
    const { balanceDate, isRange, rangeEndDate, rangeStartDate } = resolveReportDates(filters);

    let response;
    if (ACTIVITY_REPORTS.has(actualReportType)) response = await handleActivityProfitability(filters, { balanceDate, rangeEndDate, rangeStartDate }, actualReportType, language, options);
    else if (LIQUIDITY_REPORTS.has(actualReportType)) response = await handleWorkingCapitalLiquidity(filters, { balanceDate, rangeEndDate, rangeStartDate }, actualReportType, language, options);
    else if (actualReportType === "payables") response = await handlePayablesReport(filters, { rangeEndDate, rangeStartDate }, language, options);
    else if (GENERAL_REPORTS.has(actualReportType)) response = await handleGeneralReport(filters, { balanceDate, rangeEndDate, rangeStartDate }, language, options);
    else if (BALANCE_REPORTS.has(actualReportType)) {
      const apiResponse = await getBalanceSheet({ date: balanceDate, currency: filters.currency, period_count: filters.period_count || 0 }, { signal: options.signal });
      response = { success: true, reportType, isRealData: true, originalData: apiResponse.data, data: formatBalanceSheetData(apiResponse.data) };
    } else if (INCOME_REPORTS.has(actualReportType)) response = await handleVerticalIncome(filters, rangeEndDate, rangeStartDate, actualReportType, language, options);
    else if (actualReportType === "cashFlow") response = await handleCashFlow(filters, rangeEndDate, rangeStartDate, language, options);
    else {
      const locale = language === "Arabic" || language === "ar" ? "ar" : "en";
      response = {
        success: false,
        reportType,
        isRealData: false,
        data: null,
        error: locale === "ar" ? "هذا التقرير غير متاح حالياً." : "This report is not available yet.",
      };
    }

    response.userSelections = {
      level,
      reportType,
      filters: { ...filters, balanceDate, firstDayOfMonth: rangeStartDate, periodDate: balanceDate, rangeEndDate, rangeStartDate },
      hasExcelData: Boolean(filters.uploadedData),
      excelRows: filters.uploadedData?.length || 0,
      isRange,
    };
    return response;
  } catch (error) {
    if (isRequestCancelled(error)) throw error;
    console.error("Error in generateReport:", error);
    return { success: false, error: formatReportApiError(error, language), reportType, isRealData: false, data: getMockReportData(reportType, filters) };
  }
};
